import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CalendarMonth,
  Sync,
  Link as LinkIcon,
  LinkOff,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import * as outlookCalendarService from '../../services/outlookCalendarService';

const OutlookCalendar = () => {
  const [status, setStatus] = useState({ connected: false, lastSync: null, tokenExpired: false, organizationEnabled: true });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [syncDialog, setSyncDialog] = useState({ open: false, results: null });

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await outlookCalendarService.getStatus();
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load calendar status:', err);
      setError('Failed to load calendar status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setError(null);
      const response = await outlookCalendarService.getAuthUrl();
      
      // Open OAuth window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const authWindow = window.open(
        response.data.authUrl,
        'Outlook Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Listen for OAuth callback
      window.addEventListener('message', async (event) => {
        if (event.data.type === 'outlook-auth-success' && event.data.code) {
          authWindow.close();
          await completeAuth(event.data.code);
        }
      });
    } catch (err) {
      console.error('Failed to start OAuth flow:', err);
      setError(err.response?.data?.error || 'Failed to connect to Outlook calendar');
    }
  };

  const completeAuth = async (code) => {
    try {
      const response = await outlookCalendarService.completeAuth(code);
      setMessage(`Connected to Outlook calendar as ${response.data.user.displayName}`);
      await loadStatus();
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Failed to complete authentication:', err);
      setError(err.response?.data?.error || 'Failed to complete authentication');
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setMessage(null);
      
      const response = await outlookCalendarService.syncCalendar({ 
        daysAhead: 30,
        includePast: false 
      });
      
      setSyncDialog({ open: true, results: response.data.results });
      await loadStatus();
    } catch (err) {
      console.error('Failed to sync calendar:', err);
      setError(err.response?.data?.details || 'Failed to sync calendar');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Outlook calendar?')) {
      return;
    }

    try {
      await outlookCalendarService.disconnect();
      setMessage('Outlook calendar disconnected successfully');
      await loadStatus();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect calendar');
    }
  };

  const handleCloseSyncDialog = () => {
    setSyncDialog({ open: false, results: null });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  // If Outlook calendar is disabled for the organization, show a message
  if (!status.organizationEnabled) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <CalendarMonth fontSize="large" color="disabled" />
          <Typography variant="h6" color="text.secondary">
            Outlook Calendar Integration
          </Typography>
        </Box>
        <Alert severity="info">
          Outlook Calendar integration is not enabled for your organization. 
          Please contact your administrator to enable this feature.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <CalendarMonth fontSize="large" color="primary" />
        <Typography variant="h6">
          Outlook Calendar Integration
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your Outlook calendar to automatically sync meetings to the scheduler.
        Meetings with Teams or Zoom links will be scheduled for bot recording.
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Typography variant="body2" fontWeight="bold">
          Status:
        </Typography>
        {status.connected ? (
          status.tokenExpired ? (
            <Chip 
              icon={<ErrorIcon />} 
              label="Token Expired" 
              color="error" 
              size="small" 
            />
          ) : (
            <Chip 
              icon={<CheckCircle />} 
              label="Connected" 
              color="success" 
              size="small" 
            />
          )
        ) : (
          <Chip 
            label="Not Connected" 
            color="default" 
            size="small" 
          />
        )}
      </Box>

      {status.lastSync && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last synced: {new Date(status.lastSync).toLocaleString()}
        </Typography>
      )}

      <Box display="flex" gap={2} flexWrap="wrap">
        {!status.connected || status.tokenExpired ? (
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={handleConnect}
          >
            Connect Outlook Calendar
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <Sync />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Calendar'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOff />}
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </>
        )}
      </Box>

      {/* Sync Results Dialog */}
      <Dialog open={syncDialog.open} onClose={handleCloseSyncDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Calendar Sync Results</DialogTitle>
        <DialogContent>
          {syncDialog.results && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Total events found:</strong> {syncDialog.results.total}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Meetings created:</strong> {syncDialog.results.created}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Skipped:</strong> {syncDialog.results.skipped}
              </Typography>
              
              {syncDialog.results.errors && syncDialog.results.errors.length > 0 && (
                <>
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    <strong>Errors:</strong>
                  </Typography>
                  {syncDialog.results.errors.map((err, idx) => (
                    <Typography key={idx} variant="body2" color="error">
                      • {err.event}: {err.error}
                    </Typography>
                  ))}
                </>
              )}
              
              {syncDialog.results.created > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Successfully synced {syncDialog.results.created} meeting(s) from your calendar!
                </Alert>
              )}
              
              {syncDialog.results.created === 0 && syncDialog.results.total > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No new meetings to sync. All calendar events are either already scheduled or don't have meeting links.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSyncDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OutlookCalendar;
